import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowUpCircle, ArrowDownCircle, Receipt, Calendar, 
  Filter, ChevronLeft, ChevronRight, TrendingUp, TrendingDown,
  Euro, Building, Percent, ExternalLink
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MONTHS = [
  { value: 1, label: 'Gennaio' },
  { value: 2, label: 'Febbraio' },
  { value: 3, label: 'Marzo' },
  { value: 4, label: 'Aprile' },
  { value: 5, label: 'Maggio' },
  { value: 6, label: 'Giugno' },
  { value: 7, label: 'Luglio' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Settembre' },
  { value: 10, label: 'Ottobre' },
  { value: 11, label: 'Novembre' },
  { value: 12, label: 'Dicembre' }
];

export default function AdminFinancialTimeline() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [filterType, setFilterType] = useState('all'); // all, entrata, uscita
  const [page, setPage] = useState(1);

  const years = [
    new Date().getFullYear(),
    new Date().getFullYear() - 1,
    new Date().getFullYear() - 2
  ];

  useEffect(() => {
    if (token) {
      fetchTimeline();
    }
  }, [token, selectedYear, selectedMonth, filterType, page]);

  const fetchTimeline = async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/financial/timeline?anno=${selectedYear}&page=${page}&limit=50`;
      if (selectedMonth) {
        url += `&mese=${selectedMonth}`;
      }
      if (filterType !== 'all') {
        url += `&tipo=${filterType}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      console.error('Error fetching timeline:', error);
      toast.error('Errore nel caricamento della timeline');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', { 
      day: '2-digit', 
      month: 'short',
      year: 'numeric'
    });
  };

  const getMovementIcon = (movimento) => {
    switch (movimento.tipo) {
      case 'entrata':
        return <ArrowUpCircle className="w-5 h-5 text-green-500" />;
      case 'uscita':
        return <ArrowDownCircle className="w-5 h-5 text-red-500" />;
      case 'commissione':
        return <Percent className="w-5 h-5 text-orange-500" />;
      case 'tassa':
        return <Building className="w-5 h-5 text-purple-500" />;
      default:
        return <Receipt className="w-5 h-5 text-gray-500" />;
    }
  };

  const getMovementColor = (movimento) => {
    switch (movimento.tipo) {
      case 'entrata':
        return 'border-l-green-500 bg-green-50';
      case 'uscita':
        return 'border-l-red-500 bg-red-50';
      case 'commissione':
        return 'border-l-orange-500 bg-orange-50';
      case 'tassa':
        return 'border-l-purple-500 bg-purple-50';
      default:
        return 'border-l-gray-500 bg-gray-50';
    }
  };

  // Group movements by date
  const groupedMovements = {};
  if (data?.movimenti) {
    data.movimenti.forEach(m => {
      const dateKey = m.data || 'senza-data';
      if (!groupedMovements[dateKey]) {
        groupedMovements[dateKey] = [];
      }
      groupedMovements[dateKey].push(m);
    });
  }

  return (
    <AdminLayout title="Timeline Finanziaria">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="font-cinzel text-2xl text-[#1A202C]">Movimenti Finanziari</h1>
            <p className="text-sm text-gray-500">Prima nota cronologica</p>
          </div>
          <Link to="/admin/financial">
            <Button variant="outline">
              <Euro className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
          </Link>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <select
                  value={selectedYear}
                  onChange={(e) => { setSelectedYear(parseInt(e.target.value)); setPage(1); }}
                  className="border rounded-md px-3 py-1.5 text-sm"
                >
                  {years.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
                <select
                  value={selectedMonth || ''}
                  onChange={(e) => { setSelectedMonth(e.target.value ? parseInt(e.target.value) : null); setPage(1); }}
                  className="border rounded-md px-3 py-1.5 text-sm"
                >
                  <option value="">Tutto l&apos;anno</option>
                  {MONTHS.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <select
                  value={filterType}
                  onChange={(e) => { setFilterType(e.target.value); setPage(1); }}
                  className="border rounded-md px-3 py-1.5 text-sm"
                >
                  <option value="all">Tutti i movimenti</option>
                  <option value="entrata">Solo entrate</option>
                  <option value="uscita">Solo uscite</option>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        {data?.totali && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-l-4 border-l-green-500">
              <CardContent className="p-4">
                <p className="text-xs text-gray-500 uppercase">Entrate</p>
                <p className="text-xl font-bold text-green-600">{formatCurrency(data.totali.entrate)}</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-red-500">
              <CardContent className="p-4">
                <p className="text-xs text-gray-500 uppercase">Uscite</p>
                <p className="text-xl font-bold text-red-600">{formatCurrency(data.totali.uscite)}</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-orange-500">
              <CardContent className="p-4">
                <p className="text-xs text-gray-500 uppercase">Commissioni</p>
                <p className="text-xl font-bold text-orange-600">{formatCurrency(data.totali.commissioni)}</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-blue-500">
              <CardContent className="p-4">
                <p className="text-xs text-gray-500 uppercase">Saldo</p>
                <p className={`text-xl font-bold ${data.totali.saldo >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                  {formatCurrency(data.totali.saldo)}
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Timeline */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Receipt className="w-5 h-5 text-[#C5A059]" />
              Movimenti
              {data?.pagination && (
                <span className="text-sm font-normal text-gray-500">
                  ({data.pagination.total_items} totali)
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C5A059]"></div>
              </div>
            ) : Object.keys(groupedMovements).length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Receipt className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>Nessun movimento nel periodo selezionato</p>
              </div>
            ) : (
              <div className="space-y-6">
                {Object.entries(groupedMovements).map(([date, movements]) => (
                  <div key={date}>
                    <div className="sticky top-0 bg-white py-2 border-b border-gray-100 mb-3">
                      <p className="text-sm font-semibold text-gray-600">
                        {formatDate(date)}
                      </p>
                    </div>
                    <div className="space-y-2">
                      {movements.map((movimento, idx) => (
                        <div
                          key={`${movimento.riferimento_id}-${idx}`}
                          className={`flex items-center justify-between p-3 rounded-lg border-l-4 ${getMovementColor(movimento)}`}
                        >
                          <div className="flex items-center gap-3">
                            {getMovementIcon(movimento)}
                            <div>
                              <p className="font-medium text-sm text-gray-900">
                                {movimento.descrizione}
                              </p>
                              <p className="text-xs text-gray-500">
                                {movimento.categoria}
                                {movimento.codice && ` • ${movimento.codice}`}
                                {movimento.fornitore && ` • ${movimento.fornitore}`}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className={`font-bold ${movimento.segno === '+' ? 'text-green-600' : 'text-red-600'}`}>
                              {movimento.segno}{formatCurrency(movimento.importo)}
                            </p>
                            {movimento.riferimento_tipo === 'prenotazione' && (
                              <Link 
                                to={`/admin/bookings?id=${movimento.riferimento_id}`}
                                className="text-xs text-blue-500 hover:underline flex items-center gap-1 justify-end"
                              >
                                Vedi <ExternalLink className="w-3 h-3" />
                              </Link>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {data?.pagination && data.pagination.total_pages > 1 && (
              <div className="flex items-center justify-between mt-6 pt-4 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Precedente
                </Button>
                <span className="text-sm text-gray-500">
                  Pagina {page} di {data.pagination.total_pages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(data.pagination.total_pages, p + 1))}
                  disabled={page >= data.pagination.total_pages}
                >
                  Successiva
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
